import type {
  TrialBlock,
  TrialBlockResult,
  TrialBlockKind,
  TrialBlockState,
  TrialDayStatus,
  TrialDayTile,
  TrialModule,
  TrialPlan,
  TrialProgress,
  TrialPace,
  TrialSessionEndOffer,
  TrialTomorrow,
  TrialUnlockOffer,
  TrialWeekResult,
  TrialWeekSummary,
} from './types';

/** Copies the string fields a caller names, skipping anything not a string. */
function pickStrings<T extends object>(raw: Record<string, unknown>, keys: (keyof T & string)[]): T {
  const out = {} as T;
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === 'string' && value.trim()) (out as Record<string, unknown>)[key] = value;
  }
  return out;
}

const DAY_STATUSES = new Set<TrialDayStatus>(['open', 'locked', 'done']);
const BLOCK_KINDS = new Set<TrialBlockKind>([
  'review',
  'core',
  'practice',
  'contrast',
  'timed',
  'drill',
  'check',
  'week',
]);
/** Payloads written before the block-id scheme landed. */
const LEGACY_BLOCK_KINDS: Record<string, TrialBlockKind> = {
  standard: 'practice',
  'day6-check': 'check',
  'practice-exam': 'practice',
};

const BLOCK_STATES = new Set<TrialBlockState>(['locked', 'next', 'inProgress', 'done']);
const LEGACY_BLOCK_STATES: Record<string, TrialBlockState> = {
  not_started: 'next',
  in_progress: 'inProgress',
};

function asBlockKind(value: unknown): TrialBlockKind | undefined {
  if (typeof value !== 'string') return undefined;
  if (BLOCK_KINDS.has(value as TrialBlockKind)) return value as TrialBlockKind;
  return LEGACY_BLOCK_KINDS[value];
}

function asBlockState(value: unknown): TrialBlockState | undefined {
  if (typeof value !== 'string') return undefined;
  if (BLOCK_STATES.has(value as TrialBlockState)) return value as TrialBlockState;
  return LEGACY_BLOCK_STATES[value];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asDay(value: unknown): TrialDayTile['day'] | undefined {
  return value === 1 || value === 2 || value === 3 || value === 4 || value === 5 || value === 6 || value === 7
    ? value
    : undefined;
}

function unwrapPlan(raw: unknown): unknown {
  if (!isRecord(raw)) return raw;
  if (isRecord(raw.trialPlan)) return raw.trialPlan;
  if (isRecord(raw.plan) && Array.isArray(raw.plan.days)) return raw.plan;
  return raw;
}

/**
 * getTrialPlan spreads the raw plan doc under `plan`, and the backend builds the
 * day tiles by walking that same moduleOrder (trialHubViewModel: day N is
 * moduleOrder[N - 1]). The tiles themselves carry no module id, so Try 5 has to
 * read it back off the order.
 */
function parseModuleOrder(value: Record<string, unknown>): string[] {
  const plan = isRecord(value.plan) ? value.plan : undefined;
  const order = plan?.moduleOrder;
  if (!Array.isArray(order)) return [];
  return order.every((id): id is string => typeof id === 'string' && !!id) ? order : [];
}

function parseDayTile(raw: unknown): TrialDayTile | undefined {
  if (!isRecord(raw)) return undefined;
  const day = asDay(raw.day);
  if (!day) return undefined;
  if (typeof raw.status !== 'string' || !DAY_STATUSES.has(raw.status as TrialDayStatus)) {
    return undefined;
  }
  const status = raw.status as TrialDayStatus;
  const title = typeof raw.title === 'string' && raw.title.trim() ? raw.title : `Day ${day}`;
  const tryFiveAvailable =
    typeof raw.tryFiveAvailable === 'boolean' ? raw.tryFiveAvailable : status === 'locked';
  const tile: TrialDayTile = { day, status, title, tryFiveAvailable };
  if (typeof raw.lockedSummary === 'string') tile.lockedSummary = raw.lockedSummary;
  if (typeof raw.scoreLabel === 'string') tile.scoreLabel = raw.scoreLabel;
  if (typeof raw.covers === 'string') tile.covers = raw.covers;
  if (typeof raw.score === 'string') tile.score = raw.score;
  if (typeof raw.answered === 'number') tile.answered = raw.answered;
  if (typeof raw.firstAttemptAccuracy === 'number') tile.firstAttemptAccuracy = raw.firstAttemptAccuracy;
  else if (raw.firstAttemptAccuracy === null) tile.firstAttemptAccuracy = null;
  if (typeof raw.sampledCount === 'number') tile.sampledCount = raw.sampledCount;
  if (typeof raw.opensLabel === 'string') tile.opensLabel = raw.opensLabel;
  if (typeof raw.reviewAvailable === 'boolean') tile.reviewAvailable = raw.reviewAvailable;
  else if (status === 'done') tile.reviewAvailable = true;
  return tile;
}

function parseBlock(raw: unknown): TrialBlock | undefined {
  if (!isRecord(raw)) return undefined;
  if (typeof raw.id !== 'string' || !raw.id) return undefined;
  if (typeof raw.title !== 'string') return undefined;
  if (typeof raw.summaryLine !== 'string') return undefined;
  // The id carries the same vocabulary as kind, so it is the better fallback
  // than a default that would send every unknown block down the practice path.
  const kind = asBlockKind(raw.kind) ?? asBlockKind(raw.id) ?? 'practice';
  const block: TrialBlock = {
    id: raw.id,
    kind,
    title: raw.title,
    summaryLine: raw.summaryLine,
  };
  if (typeof raw.questionCount === 'number') block.questionCount = raw.questionCount;
  if (typeof raw.estimatedMinutes === 'number') block.estimatedMinutes = raw.estimatedMinutes;
  if (typeof raw.seenCount === 'number') block.seenCount = raw.seenCount;
  const state = asBlockState(raw.state);
  if (state) block.state = state;
  if (typeof raw.scoreLabel === 'string') block.scoreLabel = raw.scoreLabel;
  if (typeof raw.resumeAt === 'number') block.resumeAt = raw.resumeAt;
  if (raw.unavailable === true) block.unavailable = true;
  if (
    isRecord(raw.result) &&
    typeof raw.result.correct === 'number' &&
    typeof raw.result.total === 'number'
  ) {
    block.result = {
      correct: raw.result.correct,
      total: raw.result.total,
      firstAttemptAccuracy:
        typeof raw.result.firstAttemptAccuracy === 'number' ? raw.result.firstAttemptAccuracy : null,
    };
  }
  return block;
}

function parseModule(raw: unknown): TrialModule | null | undefined {
  if (raw == null) return null;
  if (!isRecord(raw)) return undefined;
  if (typeof raw.day !== 'number' || typeof raw.title !== 'string') return undefined;
  if (!Array.isArray(raw.blocks)) return undefined;
  const blocks: TrialBlock[] = [];
  for (const item of raw.blocks) {
    const block = parseBlock(item);
    if (!block) return undefined;
    blocks.push(block);
  }
  const module: TrialModule = { day: raw.day, title: raw.title, blocks };
  if (typeof raw.subtitle === 'string') module.subtitle = raw.subtitle;
  return module;
}

function parseOffer(raw: unknown): TrialSessionEndOffer | null | undefined {
  if (raw == null) return null;
  if (!isRecord(raw)) return undefined;
  if (typeof raw.id !== 'string' || typeof raw.title !== 'string') return undefined;
  if (typeof raw.ctaLabel !== 'string' || typeof raw.href !== 'string') return undefined;
  const offer: TrialSessionEndOffer = {
    id: raw.id,
    title: raw.title,
    ctaLabel: raw.ctaLabel,
    href: raw.href,
  };
  if (typeof raw.framingLine === 'string') offer.framingLine = raw.framingLine;
  return offer;
}

function parseSeen(raw: unknown): { seen: number; total: number } | null {
  if (raw == null || !isRecord(raw)) return null;
  if (typeof raw.seen !== 'number' || typeof raw.total !== 'number') return null;
  return { seen: raw.seen, total: raw.total };
}

function parseProgress(raw: unknown): TrialProgress | undefined {
  if (raw == null) return undefined;
  if (!isRecord(raw)) return undefined;
  if (typeof raw.daysDone !== 'number' || typeof raw.questionsAnswered !== 'number') {
    return undefined;
  }
  const readinessPercent =
    raw.readinessPercent === null
      ? null
      : typeof raw.readinessPercent === 'number'
        ? raw.readinessPercent
        : null;
  const progress: TrialProgress = {
    daysDone: raw.daysDone,
    questionsAnswered: raw.questionsAnswered,
    readinessPercent,
  };
  if (raw.timedScoreLabel === null) progress.timedScoreLabel = null;
  else if (typeof raw.timedScoreLabel === 'string') progress.timedScoreLabel = raw.timedScoreLabel;
  if (raw.checkScoreLabel === null) progress.checkScoreLabel = null;
  else if (typeof raw.checkScoreLabel === 'string') progress.checkScoreLabel = raw.checkScoreLabel;
  if (typeof raw.answered === 'number') progress.answered = raw.answered;
  if (typeof raw.readinessFirstAttempt === 'number') progress.readinessFirstAttempt = raw.readinessFirstAttempt;
  else if (raw.readinessFirstAttempt === null) progress.readinessFirstAttempt = null;
  if (typeof raw.timedScore === 'string') progress.timedScore = raw.timedScore;
  else if (raw.timedScore === null) progress.timedScore = null;
  if (typeof raw.checkScore === 'string') progress.checkScore = raw.checkScore;
  else if (raw.checkScore === null) progress.checkScore = null;
  if (typeof raw.daysLeft === 'number') progress.daysLeft = raw.daysLeft;
  if (typeof raw.diagnosticScoreLabel === 'string') progress.diagnosticScoreLabel = raw.diagnosticScoreLabel;
  if (typeof raw.weakestArea === 'string') progress.weakestArea = raw.weakestArea;
  return progress;
}

const PACE_KINDS = new Set(['fast', 'standard', 'foundation']);
const GOAL_TONES = new Set(['first-time', 'this-time']);

function parsePace(raw: unknown): TrialPace | undefined {
  if (!isRecord(raw)) return undefined;
  if (typeof raw.pace !== 'string' || !PACE_KINDS.has(raw.pace)) return undefined;
  if (typeof raw.goalTone !== 'string' || !GOAL_TONES.has(raw.goalTone)) return undefined;
  return {
    pace: raw.pace as TrialPace['pace'],
    goalTone: raw.goalTone as TrialPace['goalTone'],
    jurisdiction: typeof raw.jurisdiction === 'string' ? raw.jurisdiction : null,
    programRate: typeof raw.programRate === 'number' ? raw.programRate : null,
    studyHour: typeof raw.studyHour === 'number' ? raw.studyHour : 19,
  };
}

function numberOrNull(value: unknown): number | null | undefined {
  if (typeof value === 'number') return value;
  if (value === null) return null;
  return undefined;
}

/**
 * Every field is optional: the backend ships the week object in a separate PR,
 * and a plan missing half of it still has to render the ended screen.
 */
function parseWeek(raw: unknown): TrialWeekResult | undefined {
  if (!isRecord(raw)) return undefined;
  const week: TrialWeekResult = {};
  if (typeof raw.endedOn === 'string') week.endedOn = raw.endedOn;
  if (typeof raw.daysDone === 'number') week.daysDone = raw.daysDone;
  if (typeof raw.answered === 'number') week.answered = raw.answered;
  const readiness = numberOrNull(raw.readiness);
  if (readiness !== undefined) week.readiness = readiness;
  const diagnostic = numberOrNull(raw.diagnosticReadiness);
  if (diagnostic !== undefined) week.diagnosticReadiness = diagnostic;
  if (typeof raw.strongest === 'string' || raw.strongest === null) week.strongest = raw.strongest;
  if (typeof raw.weakest === 'string' || raw.weakest === null) week.weakest = raw.weakest;
  if (isRecord(raw.check) && typeof raw.check.taken === 'boolean') {
    week.check = {
      taken: raw.check.taken,
      correct: numberOrNull(raw.check.correct) ?? null,
      total: typeof raw.check.total === 'number' ? raw.check.total : 20,
    };
  }
  if (
    isRecord(raw.misses) &&
    typeof raw.misses.reviewed === 'number' &&
    typeof raw.misses.total === 'number'
  ) {
    week.misses = { reviewed: raw.misses.reviewed, total: raw.misses.total };
  }
  return Object.keys(week).length > 0 ? week : undefined;
}

const OFFER_KINDS = new Set(['plan-line', 'session-end', 'check']);

function parseUnlockOffer(raw: unknown): TrialUnlockOffer | undefined {
  if (!isRecord(raw)) return undefined;
  if (typeof raw.kind !== 'string' || !OFFER_KINDS.has(raw.kind)) return undefined;
  if (typeof raw.text !== 'string') return undefined;
  return {
    kind: raw.kind as TrialUnlockOffer['kind'],
    text: raw.text,
    lockedCount: typeof raw.lockedCount === 'number' ? raw.lockedCount : 0,
    lockedQuestionCount: typeof raw.lockedQuestionCount === 'number' ? raw.lockedQuestionCount : 0,
  };
}

export function parseTrialPlan(raw: unknown): TrialPlan | undefined {
  const value = unwrapPlan(raw);
  if (!isRecord(value)) return undefined;
  if (typeof value.currentDay !== 'number' || !Number.isFinite(value.currentDay)) return undefined;
  if (!Array.isArray(value.days) || value.days.length !== 7) return undefined;

  const moduleOrder = parseModuleOrder(value);
  const days: TrialDayTile[] = [];
  const seenDays = new Set<number>();
  for (const item of value.days) {
    const tile = parseDayTile(item);
    if (!tile || seenDays.has(tile.day)) return undefined;
    seenDays.add(tile.day);
    const moduleId = moduleOrder[tile.day - 1];
    if (moduleId) tile.moduleId = moduleId;
    days.push(tile);
  }

  const module = parseModule(value.module);
  if (module === undefined) return undefined;
  const sessionEndOffer = parseOffer(value.sessionEndOffer);
  if (sessionEndOffer === undefined) return undefined;

  const plan: TrialPlan = {
    active: value.active === true,
    currentDay: value.currentDay,
    days,
    module,
    sessionEndOffer,
  };
  const practiceExamSeen = parseSeen(value.practiceExamSeen);
  if (practiceExamSeen) plan.practiceExamSeen = practiceExamSeen;
  else if (value.practiceExamSeen === null) plan.practiceExamSeen = null;

  if (typeof value.daysLeft === 'number') plan.daysLeft = value.daysLeft;
  if (typeof value.whyToday === 'string') plan.whyToday = value.whyToday;
  if (value.frozen === true) plan.frozen = true;
  if (value.ended === true) plan.ended = true;
  const week = parseWeek(value.week);
  if (week) plan.week = week;

  const progress = parseProgress(value.progress);
  if (progress) plan.progress = progress;

  if (typeof value.adapts === 'string') plan.adapts = value.adapts;
  const pace = parsePace(value.pace);
  if (pace) plan.pace = pace;
  const offer = parseUnlockOffer(value.offer);
  if (offer) plan.offer = offer;
  if (isRecord(value.tomorrow)) {
    const tomorrow = pickStrings<TrialTomorrow>(value.tomorrow, [
      'opensTitle',
      'reminderLabel',
      'afterTrialLine',
    ]);
    if (typeof value.tomorrow.reviewsWaiting === 'number') {
      tomorrow.reviewsWaiting = value.tomorrow.reviewsWaiting;
    }
    if (Object.keys(tomorrow).length > 0) plan.tomorrow = tomorrow;
  }
  if (isRecord(value.weekSummary)) {
    const summary = pickStrings<TrialWeekSummary>(value.weekSummary, [
      'strongest',
      'weakest',
      'readinessLine',
      'nextLicensingStep',
    ]);
    if (Object.keys(summary).length > 0) plan.weekSummary = summary;
  }

  if ('dashboardOffer' in value) {
    const dashboardOffer = parseOffer(value.dashboardOffer);
    if (dashboardOffer === undefined) return undefined;
    plan.dashboardOffer = dashboardOffer;
  }

  return plan;
}
